import {
  isPortalEnabledByAvailability,
  isPortalExplicitlyEnabledByOverride,
} from 'src/shared/config/portalAvailabilityRules'
import {
  getPortalById,
  getPortalDefaultHandoffRoles,
  getPortalDefaultPath,
  getPortalPreferenceRoles,
  PORTAL_CHOOSER_ID,
  PORTAL_IDS,
  WELLNESS_PORTAL_ROLES as WELLNESS_PORTAL_ROLE_LIST,
} from 'src/shared/config/portalConfig'

const WELLNESS_PORTAL_ROLES = new Set(WELLNESS_PORTAL_ROLE_LIST)
const REDIRECT_PORTAL_ID = 'redirect'
const LEGACY_WELLNESS_FLOW_ID = 'wellness'

const CONSUMER_PORTAL_DEFINITION = {
  ...getPortalById(PORTAL_IDS.CONSUMER),
  id: PORTAL_IDS.CONSUMER,
  roles: WELLNESS_PORTAL_ROLES,
  path: getPortalDefaultPath(PORTAL_IDS.CONSUMER),
}

// Applicant/prospect roles that should make the portal chooser visible but
// must not silently default the user into a gate page. ROLE_CREATOR is the
// mentor applicant role; the actual practitioner default starts at ROLE_TEACHER
// or clinical roles in MENTOR_PORTAL_ROLES.
const CHOOSER_ONLY_PORTAL_ROLES = new Set(['ROLE_CREATOR'])

function toRoleSet(roles) {
  return new Set(Array.isArray(roles) ? roles : [])
}

function buildHandoffPortalDefinition(portalId) {
  const portal = getPortalById(portalId)
  return {
    ...portal,
    id: portalId,
    roles: toRoleSet(getPortalDefaultHandoffRoles(portalId)),
    path: (userRoles) => getPortalDefaultPath(portalId, userRoles),
  }
}

const ROLE_BASED_PORTAL_PREFERENCES = [
  PORTAL_IDS.MENTOR,
  PORTAL_IDS.CLINIC,
  PORTAL_IDS.SUPPORT,
  PORTAL_IDS.BUSINESS,
  PORTAL_IDS.PATIENT,
  PORTAL_IDS.PATIENT_PROSPECT,
].map((portalId) => ({
  portalId,
  roles: toRoleSet(getPortalPreferenceRoles(portalId)),
}))

const DEFAULT_PORTAL_DEFINITIONS = [
  PORTAL_IDS.SUPPORT,
  PORTAL_IDS.MENTOR,
  PORTAL_IDS.CLINIC,
  PORTAL_IDS.BUSINESS,
  PORTAL_IDS.PATIENT,
  PORTAL_IDS.PATIENT_PROSPECT,
  PORTAL_IDS.CLINIC_ASSISTANT,
  PORTAL_IDS.KIOSK,
  PORTAL_IDS.DISPLAY,
].map(buildHandoffPortalDefinition)

function hasAnyRole(userRoles, candidateRoles) {
  return userRoles.some((role) => candidateRoles.has(role))
}

function resolvePortalPath(portal, userRoles) {
  return typeof portal.path === 'function' ? portal.path(userRoles) : portal.path
}

function normalizeRedirectPath(path) {
  return typeof path === 'string' && path.trim() ? path.trim() : ''
}

export function isWellnessRedirectPath(path) {
  const redirectPath = normalizeRedirectPath(path)
  if (!redirectPath) return false

  const pathname = redirectPath.split(/[?#]/)[0]
  return (
    pathname === '/' ||
    pathname === '/wellness' ||
    pathname.startsWith('/wellness/') ||
    pathname === '/onboarding' ||
    pathname.startsWith('/onboarding/')
  )
}

function getDefaultPortalMatches(userRoles, options = {}) {
  const portalAvailabilityOverrides = options?.portalAvailabilityOverrides
  const matches = DEFAULT_PORTAL_DEFINITIONS.filter(
    (portal) =>
      hasAnyRole(userRoles, portal.roles) &&
      isPortalEnabledByAvailability(portal, userRoles, portalAvailabilityOverrides),
  )

  if (
    hasAnyRole(userRoles, CONSUMER_PORTAL_DEFINITION.roles) &&
    isPortalExplicitlyEnabledByOverride(CONSUMER_PORTAL_DEFINITION, portalAvailabilityOverrides) &&
    isPortalEnabledByAvailability(CONSUMER_PORTAL_DEFINITION, userRoles, portalAvailabilityOverrides)
  ) {
    return [...matches, CONSUMER_PORTAL_DEFINITION]
  }

  return matches
}

export function resolveRoleBasedPreferredPortalId(authoritiesLike, options = {}) {
  const userRoles = normalizeAuthorities(authoritiesLike)
  if (userRoles.length === 0) return null

  const availablePortalIds = Array.isArray(options?.availablePortalIds)
    ? new Set(options.availablePortalIds.filter((portalId) => typeof portalId === 'string'))
    : new Set(getDefaultPortalMatches(userRoles, options).map((portal) => portal.id))

  for (const preference of ROLE_BASED_PORTAL_PREFERENCES) {
    if (
      availablePortalIds.has(preference.portalId) &&
      hasAnyRole(userRoles, preference.roles)
    ) {
      return preference.portalId
    }
  }

  return null
}

function resolvePairedKioskPreference(matches, options = {}) {
  const hasClinicAssistantPortal = matches.some(
    (portal) => portal.id === PORTAL_IDS.CLINIC_ASSISTANT,
  )
  const hasKioskPortal = matches.some((portal) => portal.id === PORTAL_IDS.KIOSK)

  if (!hasClinicAssistantPortal || !hasKioskPortal || matches.length !== 2) {
    return null
  }

  const lastChosenFlow =
    typeof options?.lastChosenFlow === 'string' ? options.lastChosenFlow.trim() : ''
  const kioskConfig = options?.kioskConfig || null
  const roleCaConfig = options?.roleCaConfig || null

  // Prefer the last-used flow, else fall back to whichever portal has a saved
  // config present. Flow is implied by the portal now, so we no longer key off
  // a FLOW_ID field — just the presence of a stored config.
  if (lastChosenFlow === PORTAL_IDS.KIOSK || kioskConfig) {
    return {
      portalId: PORTAL_IDS.KIOSK,
      path: getPortalDefaultPath(PORTAL_IDS.KIOSK, []),
    }
  }

  if (lastChosenFlow === PORTAL_IDS.CLINIC_ASSISTANT || roleCaConfig) {
    return {
      portalId: PORTAL_IDS.CLINIC_ASSISTANT,
      path: getPortalDefaultPath(PORTAL_IDS.CLINIC_ASSISTANT, []),
    }
  }

  return null
}

function parseJsonSafe(value) {
  if (typeof value !== 'string' || !value.trim()) {
    return null
  }

  try {
    return JSON.parse(value)
  } catch (error) {
    return null
  }
}

export function normalizeAuthorities(authoritiesLike) {
  if (Array.isArray(authoritiesLike)) {
    return authoritiesLike.filter((value) => typeof value === 'string' && value.trim())
  }

  if (typeof authoritiesLike === 'string') {
    const parsedAuthorities = parseJsonSafe(authoritiesLike)
    return Array.isArray(parsedAuthorities)
      ? parsedAuthorities.filter((value) => typeof value === 'string' && value.trim())
      : []
  }

  return []
}

export function resolveDirectPortalHandoff(authoritiesLike, options = {}) {
  const resolvedHome = resolveRoleBasedPortalHome(authoritiesLike, options)

  if (
    !resolvedHome ||
    resolvedHome.portalId === PORTAL_IDS.CONSUMER ||
    resolvedHome.portalId === PORTAL_CHOOSER_ID
  ) {
    return null
  }

  return resolvedHome
}

export function resolveRoleBasedPortalHome(authoritiesLike, options = {}) {
  const userRoles = normalizeAuthorities(authoritiesLike)

  if (userRoles.length === 0) {
    return null
  }

  const defaultPortalMatches = getDefaultPortalMatches(userRoles, options)
  const pairedKioskPreference = resolvePairedKioskPreference(defaultPortalMatches, options)

  if (pairedKioskPreference) {
    return pairedKioskPreference
  }

  if (defaultPortalMatches.length === 1) {
    const portal = defaultPortalMatches[0]
    return { portalId: portal.id, path: resolvePortalPath(portal, userRoles) }
  }

  if (defaultPortalMatches.length > 1 || hasAnyRole(userRoles, CHOOSER_ONLY_PORTAL_ROLES)) {
    return { portalId: PORTAL_CHOOSER_ID, path: '/portals' }
  }

  const hasWellnessPortalRole = hasAnyRole(userRoles, WELLNESS_PORTAL_ROLES)
  const hasEnabledWellnessPortal =
    hasWellnessPortalRole &&
    isPortalEnabledByAvailability(
      CONSUMER_PORTAL_DEFINITION,
      userRoles,
      options?.portalAvailabilityOverrides,
    )
  const hasOnlyWellnessRoles = userRoles.every((role) => WELLNESS_PORTAL_ROLES.has(role))

  if (hasEnabledWellnessPortal && hasOnlyWellnessRoles) {
    return { portalId: PORTAL_IDS.CONSUMER, path: getPortalDefaultPath(PORTAL_IDS.CONSUMER) }
  }

  if (!hasOnlyWellnessRoles) {
    return { portalId: PORTAL_CHOOSER_ID, path: '/portals' }
  }

  return null
}

export function resolvePostLoginNavigation(authoritiesLike, options = {}) {
  const roleBasedDestination =
    resolveRoleBasedPortalHome(authoritiesLike, options) ||
    { portalId: PORTAL_CHOOSER_ID, path: '/portals' }
  const redirectPath = normalizeRedirectPath(options?.redirectPath)

  if (
    redirectPath &&
    (!isWellnessRedirectPath(redirectPath) ||
      roleBasedDestination.portalId === PORTAL_IDS.CONSUMER)
  ) {
    return { portalId: REDIRECT_PORTAL_ID, path: redirectPath }
  }

  return roleBasedDestination
}

export function hasNonWellnessPortalRole(authoritiesLike) {
  const userRoles = normalizeAuthorities(authoritiesLike)
  if (userRoles.length === 0) return false
  return (
    getDefaultPortalMatches(userRoles).length > 0 ||
    hasAnyRole(userRoles, CHOOSER_ONLY_PORTAL_ROLES) ||
    userRoles.some((role) => !WELLNESS_PORTAL_ROLES.has(role))
  )
}

export function rememberPortalHandoff(portalId) {
  if (typeof window === 'undefined' || typeof portalId !== 'string' || !portalId.trim()) {
    return
  }

  sessionStorage.setItem('chosenSomeFlowInThisSession', 'true')

  if (portalId === PORTAL_IDS.CONSUMER || portalId === LEGACY_WELLNESS_FLOW_ID) {
    sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
    localStorage.removeItem('lastChosenFlow')
    return
  }

  if (portalId === PORTAL_CHOOSER_ID || portalId === REDIRECT_PORTAL_ID) {
    return
  }

  localStorage.setItem('lastChosenFlow', portalId)
}

export function preparePostLoginPortalState(options = {}) {
  if (typeof window === 'undefined') {
    return
  }

  const explicitWellness = options?.explicitWellness === true

  try {
    sessionStorage.removeItem('chosenSomeFlowInThisSession')
    sessionStorage.removeItem('portalAutoNavigatedToAtFlow')

    if (explicitWellness) {
      sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
    } else {
      sessionStorage.removeItem('explicitlyChoseConsumerPortal')
    }
  } catch {
    // Storage may be blocked; routing still falls back to role-based decisions.
  }

  try {
    const lastChosenFlow = localStorage.getItem('lastChosenFlow')
    if (
      lastChosenFlow === PORTAL_IDS.CONSUMER ||
      lastChosenFlow === LEGACY_WELLNESS_FLOW_ID
    ) {
      localStorage.removeItem('lastChosenFlow')
    }
  } catch {
    // Non-fatal.
  }
}

export function shouldSkipSecureSetupOnPortalRoutingPage(authoritiesLike, options = {}) {
  const pathname = typeof options?.pathname === 'string' ? options.pathname.trim() : ''
  const explicitlyChoseConsumerPortal = options?.explicitlyChoseConsumerPortal === true

  if (pathname !== '/' && pathname !== '') {
    return false
  }

  if (explicitlyChoseConsumerPortal) {
    return false
  }

  return Boolean(resolveDirectPortalHandoff(authoritiesLike, options))
}
